import type { V3QuoteSku, V3QuoteSubtotal, InvoiceSummary } from '../types';

function roundAmount(amount: number): number {
  return Math.round((amount + Number.EPSILON) * 100) / 100;
}

/**
 * Discriminated union for display rows in the SKU editor.
 * Allows rendering ungrouped SKUs, section headers, grouped SKUs, and section subtotals.
 */
export type DisplayRow =
  | { type: 'sku'; sku: V3QuoteSku; skuIndex: number }
  | { type: 'section-header'; subtotal: V3QuoteSubtotal; subtotalIndex: number }
  | { type: 'section-sum'; subtotal: V3QuoteSubtotal; subtotalIndex: number; amount: number };

/**
 * Calculate line total for a single SKU row
 */
export function calculateLineTotal(qty: number, unitPrice: number, discount = 0): number {
  return roundAmount(qty * unitPrice * (100 - discount) / 100);
}

/**
 * Calculate subtotal amount for a group of SKUs
 */
export function calculateSubtotalAmount(skus: V3QuoteSku[], subtotalId: string): number {
  return skus
    .filter((sku) => sku.subtotal_id === subtotalId)
    .reduce((sum, sku) => sum + calculateLineTotal(sku.quantity, sku.unit_price, sku.discount), 0);
}

/**
 * Calculate grand total across all SKU rows
 */
export function calculateGrandTotal(skus: V3QuoteSku[]): number {
  return skus.reduce(
    (sum, sku) => sum + calculateLineTotal(sku.quantity, sku.unit_price, sku.discount),
    0
  );
}

/**
 * Reorder SKUs by moving an item from one index to another
 */
export function reorderSkus(skus: V3QuoteSku[], fromIndex: number, toIndex: number): V3QuoteSku[] {
  const result = [...skus];
  const [removed] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, removed);
  return result;
}

/**
 * Check if a SKU row is a note row (description-only)
 */
export function isNoteRow(sku: V3QuoteSku): boolean {
  return sku.sku === '__note__';
}

/**
 * Create a new empty product row
 */
export function createEmptyProductRow(subtotalId?: string): V3QuoteSku {
  return {
    sku: '',
    name: '',
    description: '',
    quantity: 1,
    unit_price: 0,
    discount: 0,
    subtotal_id: subtotalId,
  };
}

/**
 * Create a new note row
 */
export function createNoteRow(subtotalId?: string): V3QuoteSku {
  return {
    sku: '__note__',
    name: '',
    description: '',
    quantity: 0,
    unit_price: 0,
    discount: 0,
    subtotal_id: subtotalId,
  };
}

/**
 * Check if a SKU row is a section marker
 */
export function isSectionRow(sku: V3QuoteSku): boolean {
  return sku.sku === '__section__';
}

/**
 * Create a section marker SKU (used for potential inline editing)
 */
export function createSectionRow(subtotalId: string): V3QuoteSku {
  return {
    sku: '__section__',
    name: '',
    description: '',
    quantity: 0,
    unit_price: 0,
    discount: 0,
    subtotal_id: subtotalId,
  };
}

/**
 * Build display rows from SKUs and subtotals.
 * Pattern: ungrouped SKUs first, then for each subtotal: header → grouped SKUs → sum row.
 */
export function buildDisplayRows(skus: V3QuoteSku[], subtotals: V3QuoteSubtotal[]): DisplayRow[] {
  const rows: DisplayRow[] = [];

  // Ungrouped SKUs (no subtotal_id or subtotal_id not in any subtotal)
  const subtotalIds = new Set(subtotals.map((s) => s._id));
  skus.forEach((sku, skuIndex) => {
    if (!sku.subtotal_id || !subtotalIds.has(sku.subtotal_id)) {
      rows.push({ type: 'sku', sku, skuIndex });
    }
  });

  // Grouped sections
  subtotals.forEach((subtotal, subtotalIndex) => {
    rows.push({ type: 'section-header', subtotal, subtotalIndex });

    const groupedSkus = skus
      .map((sku, skuIndex) => ({ sku, skuIndex }))
      .filter(({ sku }) => sku.subtotal_id === subtotal._id);

    for (const { sku, skuIndex } of groupedSkus) {
      rows.push({ type: 'sku', sku, skuIndex });
    }

    const amount = calculateSubtotalAmount(skus, subtotal._id || '');
    rows.push({ type: 'section-sum', subtotal, subtotalIndex, amount });
  });

  return rows;
}

/**
 * Calculate invoiced quantities for each SKU row.
 * Matches invoice line items to quote SKUs by composite key (sku + unit_price + discount).
 * Skips cancelled invoices and note rows.
 * Returns an array of invoiced quantities matching the skus array indices.
 */
export function calculateInvoicedQuantities(
  skus: V3QuoteSku[],
  invoices: InvoiceSummary[]
): number[] {
  const invoicedQtyMap: Record<string, number> = {};

  for (const invoice of invoices) {
    if (invoice.status === 'cancelled' || !Array.isArray(invoice.skus)) continue;

    for (const invoiceSku of invoice.skus) {
      if (invoiceSku.sku === '__note__') continue;

      const key = `${invoiceSku.sku}$${(invoiceSku.unit_price ?? 0).toFixed(2)}-${invoiceSku.discount || 0}`;
      invoicedQtyMap[key] = (invoicedQtyMap[key] || 0) + invoiceSku.quantity;
    }
  }

  const remainingMap = { ...invoicedQtyMap };

  return skus.map((sku) => {
    if (!sku.sku || sku.sku === '__note__') return 0;

    const key = `${sku.sku}$${(sku.unit_price ?? 0).toFixed(2)}-${sku.discount || 0}`;
    const available = remainingMap[key] || 0;

    if (available <= 0) return 0;

    if (available < sku.quantity) {
      remainingMap[key] = 0;
      return available;
    }

    remainingMap[key] = available - sku.quantity;
    return sku.quantity;
  });
}
